/**
 * One-time reveal of MFA recovery codes.
 *
 * The backend returns these plaintext exactly once (on confirm or on
 * regenerate) and never again — so this panel makes them easy to save with
 * copy-to-clipboard and download-as-text affordances before the user
 * dismisses them. Shared by the forced-enrolment page and the profile
 * security card.
 */
import { useState } from 'react';
import { colors } from '../../styles/theme';

interface Props {
  readonly codes: string[];
}

export default function RecoveryCodesPanel({ codes }: Props): JSX.Element {
  const [copied, setCopied] = useState(false);

  const asText = codes.join('\n');

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard API can be blocked (insecure context / permission) —
      // the codes are still visible on screen and downloadable.
      setCopied(false);
    }
  };

  const download = (): void => {
    const blob = new Blob([`${asText}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qmi-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div
        className="rounded-lg p-3 mb-3 text-xs"
        style={{ background: colors.warningBg, color: colors.warning,
                 border: `1px solid ${colors.border}` }}
      >
        Save these recovery codes now. Each one works once if you lose access
        to your authenticator. They will not be shown again.
      </div>

      <ul
        className="grid grid-cols-2 gap-2 p-3 rounded-lg mb-3"
        style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      >
        {codes.map((c) => (
          <li key={c} className="text-sm font-mono tracking-wider" style={{ color: colors.ink }}>
            {c}
          </li>
        ))}
      </ul>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { copy(); }}
          className="text-xs px-3 py-1.5 rounded-lg font-semibold"
          style={{ background: colors.canvas, color: colors.inkSoft,
                   border: `1px solid ${colors.border}` }}
        >
          {copied ? 'Copied' : 'Copy codes'}
        </button>
        <button
          type="button"
          onClick={download}
          className="text-xs px-3 py-1.5 rounded-lg font-semibold"
          style={{ background: colors.canvas, color: colors.inkSoft,
                   border: `1px solid ${colors.border}` }}
        >
          Download .txt
        </button>
      </div>
    </div>
  );
}
